import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Users, Building, Grid3X3, ClipboardList, CheckCircle2, GraduationCap,
  Briefcase, Wrench, AlertTriangle, Loader2, Plus, ArrowRight, Bell,
  Sparkles, Clock, Activity, FileText, Network, History,
} from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { recruitmentClientApi } from '@/lib/recruitmentApi';
import { formatApiError, api } from '@/lib/api';
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip as ChartTooltip,
} from 'recharts';

// Founder UAT 2026-06 — vivid executive chart palette (no line charts).
const VIVID = ['#3B82F6', '#F97316', '#EF4444', '#EAB308', '#10B981', '#8B5CF6', '#EC4899', '#06B6D4'];
import { ClientLogo } from '@/components/shared/ClientLogo';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { ClientEvaluationRequestDialog } from '@/components/client/ClientEvaluationRequestDialog';
import { OVERLAY_CLASS, FOCAL_POS, companyInitials } from '@/components/admin/ClientBrandingSection';

/**
 * Sprint A1 — Real-data Client Admin dashboard.
 *
 * No mock data. Every metric is derived from a single in-flight fetch of the
 * APIs the platform already exposes. TalentFix has no backend yet, so its
 * card is intentionally a "not configured" placeholder.
 *
 * Loaded in parallel: company, users, departments, evaluation-requests,
 * reports, recruitment-processes, talent-train users, talenttrain alerts,
 * intelligence overview.
 */

const SUITE_PATHS = {
  intelligence: '/client/talentintelligence',
  climara: '/client/climara',
  talentmap: '/client/talentmap',
  talentcheck: '/client/talentcheck',
  talentstart: '/client/talentstart',
  talentheart: '/client/talentheart',
  talentfix: '/client/talentfix',
  talentcompetency: '/client/talentcompetency',
  talenttrain: '/client/talenttrain',
};

const SUITE_NAMES = {
  intelligence: 'TalentIntelligence',
  workforce_records: 'Workforce Records',
  climara: 'Climara',
  talentmap: 'TalentMap',
  talentcheck: 'TalentCheck',
  talentstart: 'TalentStart',
  talentheart: 'TalentHeart',
  talentfix: 'TalentFix',
  talentcompetency: 'TalentCompetency',
  talenttrain: 'TalentTrain',
  talentexit: 'TalentExit',
  talentperformance: 'TalentPerformance',
  succession: 'Succession',
};

const DAYS = (iso) => {
  if (!iso) return null;
  try {
    const diff = Date.now() - new Date(iso).getTime();
    return Math.floor(diff / 86_400_000);
  } catch { return null; }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(getUiLocale()); } catch { return iso; }
};

function KpiCard({ icon: Icon, label, value, subValue, hint, tone = 'primary', testid, to, onClick }) {
  const tones = {
    primary:     'from-primary/15 to-primary/5 text-primary',
    success:     'from-success/15 to-success/5 text-success',
    warning:     'from-warning/15 to-warning/5 text-warning',
    info:        'from-info/15 to-info/5 text-info',
    destructive: 'from-destructive/15 to-destructive/5 text-destructive',
    muted:       'from-muted to-muted/40 text-muted-foreground',
  };
  const interactive = !!to || !!onClick;
  const Wrapper = to ? Link : 'div';
  const wrapperProps = to ? { to } : (onClick ? { onClick, role: 'button', tabIndex: 0 } : {});
  return (
    <Wrapper {...wrapperProps} className={interactive ? 'block focus:outline-none focus:ring-2 focus:ring-primary rounded-xl' : 'block'}>
      <Card
        className={`p-4 shadow-card transition-smooth ${interactive ? 'hover:shadow-elevated cursor-pointer' : ''}`}
        data-testid={testid}
      >
        <div className="flex items-start gap-3">
          <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${tones[tone]}`}>
            <Icon className="h-5 w-5" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] sm:text-xs uppercase tracking-wide text-muted-foreground font-medium leading-tight break-normal" title={label}>{label}</p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <p className="text-2xl font-bold tabular-nums">{value}</p>
              {subValue != null && (
                <p className="text-sm font-semibold text-muted-foreground tabular-nums">/ {subValue}</p>
              )}
            </div>
            {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
          </div>
          {interactive && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-1" />}
        </div>
      </Card>
    </Wrapper>
  );
}

export default function ClientDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reqOpen, setReqOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [
        company, employees, departments, evalReqs, reports,
        recruitments, training, trainingAlerts, overview, onboardingDash,
        tfCases, climaraCamps, clientNotifs, tcWorkspace, suiteActivity,
        serverActivity, positions, assignmentsOverview, deadlineAlerts,
      ] = await Promise.all([
        clientApi.getMyCompany().catch(() => null),
        clientApi.listCollaborators().catch(() => []),
        clientApi.listDepartments().catch(() => []),
        clientApi.listEvaluationRequests().catch(() => []),
        clientApi.listReports().catch(() => []),
        recruitmentClientApi.list().catch(() => []),
        clientApi.ttListUsers().catch(() => ({ users: [] })),
        clientApi.ttTrainingAlerts().catch(() => ({ items: [], count: 0 })),
        clientApi.intelligenceOverview().catch(() => null),
        clientApi.onboardingDashboard().catch(() => null),
        clientApi.talentFixListCases({}).catch(() => null),
        clientApi.climaraCampaigns().catch(() => ({ items: [] })),
        api.get('/client/notifications').then((r) => r.data).catch(() => []),
        api.get('/client/recruitment-workspace').then((r) => r.data).catch(() => null),
        api.get('/client/suite-activity').then((r) => r.data).catch(() => ({})),
        api.get('/client/recent-activity').then((r) => r.data).catch(() => ({ items: [] })),
        api.get('/client/positions').then((r) => r.data).catch(() => []),
        api.get('/client/assignments-overview').then((r) => r.data).catch(() => []),
        api.get('/client/deadline-alerts').then((r) => r.data).catch(() => null),
      ]);
      setData({
        company, employees, departments, evalReqs, reports,
        recruitments, training, trainingAlerts, overview, onboardingDash,
        tfCases, climaraCamps: climaraCamps?.items || [],
        clientNotifs: clientNotifs || [],
        tcWorkspace,
        suiteActivity: suiteActivity || {},
        serverActivity: serverActivity?.items || [],
        positions: Array.isArray(positions) ? positions : [],
        assignmentsOverview: Array.isArray(assignmentsOverview) ? assignmentsOverview : [],
        deadlineAlerts,
      });
    } catch (e) {
      toast.error(t('toasts.loadFailed', { defaultValue: 'Could not load dashboard' }),
                  { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // ------------ Metrics --------------------------------------------------
  const metrics = useMemo(() => {
    if (!data) return null;

    const employeeCount = data.employees.filter((u) =>
      ['evaluated', 'client_admin'].includes(u.role)
      && !u.external_candidate && !u.recruitment_candidate
      && u.employment_status !== 'former'
      && !['inactive', 'disabled'].includes(u.status)).length;
    const departmentCount = data.departments.length;
    const activeSuites = data.company?.suite_codes || user?.company_suite_codes || [];

    // Pending Evaluations — split into two numbers per user choice
    const pendingApproval = data.evalReqs.filter((r) => r.status === 'pending').length;
    const inProgress      = data.evalReqs.filter((r) => r.status === 'approved').length;

    // Completed Evaluations — CTH-003: same definition as the analytics chart
    // (non-survey evaluation assignments submitted/completed).
    const completedEvals = (data.assignmentsOverview || []).filter(
      (a) => !a.survey_id && ['submitted', 'completed'].includes(a.status)).length;

    // Active Training Programs — collaborators with at least one non-completed action
    const trainingUsers = data.training?.users || [];
    const activeTraining = trainingUsers.filter((r) => {
      const ac = r.action_counts || {};
      return (ac.planned || 0) + (ac.in_progress || 0) > 0;
    }).length;

    // Active Onboarding Processes — real onboarding_assignments (Sprint A2).
    // Falls back to legacy recruitment-process proxy when the onboarding
    // suite is not contracted / no templates exist yet.
    const onbActive = data.onboardingDash?.kpis?.active;
    const activeOnboardings = (onbActive != null)
      ? onbActive
      : data.recruitments.filter(
          (p) => (p.stage || 0) > 0 && (p.stage || 0) < 11,
        ).length;

    // UAT 07/18 — F: real TalentFix module state (no hardcoded text).
    const tfContracted = activeSuites.includes('talentfix');
    const tfItems = data.tfCases?.items || null;
    const tfOpen = tfItems
      ? tfItems.filter((c) => ['pending', 'accepted', 'in_progress'].includes(c.status)).length
      : null;
    const talentFix = !tfContracted
      ? { state: 'not_contracted' }
      : (tfItems === null)
        ? { state: 'unavailable' }
        : (tfOpen > 0 ? { state: 'active', open: tfOpen } : { state: 'no_active_cases', open: 0 });

    return {
      employeeCount, departmentCount,
      activeSuiteCount: activeSuites.length,
      pendingApproval, inProgress,
      completedEvals,
      activeTraining, trainingTotal: trainingUsers.length,
      activeOnboardings,
      activeSuites,
      talentFix,
    };
  }, [data, user?.company_suite_codes]);

  // ------------ Executive alerts -----------------------------------------
  const alerts = useMemo(() => {
    if (!data) return [];
    const out = [];

    // TalentStart remediation — notification-backed executive alerts follow the
    // shared Climara lifecycle: NEW highlight until viewed, dismissible, and
    // resolved/expired alerts are already filtered out server-side.
    const isES = !(i18n.language || '').startsWith('en');
    (data.clientNotifs || [])
      .filter((n) => ['report_released_to_client', 'climara_report_released_alert', 'onboarding_completed'].includes(n.type))
      .slice(0, 6)
      .forEach((n) => {
        out.push({
          key: `notif-${n.id}`,
          nid: n.id,
          isNew: !!n.is_new,
          dismissible: true,
          severity: 'primary',
          icon: FileText,
          label: (isES ? n.title : (n.title_en || n.title))
            || t('pages.clientDashboard.alerts.newReportFallback', { defaultValue: 'Nuevo informe disponible' }),
          to: n.link || '/client/reports',
        });
      });

    // 0. Item 10 (UAT iter 2) — active Climara campaign is surfaced
    //    immediately after approval, even before the first responses arrive.
    (data.climaraCamps || []).filter((c) => c.status === 'active').forEach((c) => {
      const k = c.kpis || {};
      out.push({
        key: `climara-active-${c.id}`,
        severity: 'info',
        icon: Activity,
        label: t('pages.clientDashboard.alerts.climaraActive', {
          name: c.name,
          done: k.completed ?? 0,
          total: k.invited ?? 0,
          defaultValue: 'Climara campaign active: “{{name}}” — {{done}}/{{total}} responses received',
        }),
        to: '/client/climara',
      });
    });

    // CTH-031 — due-date alerts (7 days ahead / today / overdue) for
    // TalentTrain actions and TalentStart onboarding routes.
    const da = data.deadlineAlerts;
    if (da) {
      [['training', '/client/talenttrain', 'TalentTrain'],
       ['onboarding', '/client/talentstart', 'TalentStart']].forEach(([kind, to, brandName]) => {
        const c = da.counts?.[kind] || {};
        if (c.overdue > 0) {
          out.push({ key: `${kind}-overdue`, severity: 'destructive', icon: AlertTriangle,
            label: isES ? `${brandName}: ${c.overdue} con fecha vencida` : `${brandName}: ${c.overdue} past due date`, to });
        }
        if (c.due_today > 0) {
          out.push({ key: `${kind}-due-today`, severity: 'warning', icon: Clock,
            label: isES ? `${brandName}: ${c.due_today} vence(n) hoy` : `${brandName}: ${c.due_today} due today`, to });
        }
        if (c.due_soon > 0) {
          out.push({ key: `${kind}-due-soon`, severity: 'info', icon: Clock,
            label: isES ? `${brandName}: ${c.due_soon} vence(n) en los próximos 7 días` : `${brandName}: ${c.due_soon} due within 7 days`, to });
        }
      });
    }

    // 1. Overdue evaluations: pending request > 7 days old
    const overdueEvals = data.evalReqs.filter((r) =>
      r.status === 'pending' && (DAYS(r.created_at) || 0) > 7,
    );
    if (overdueEvals.length > 0) {
      out.push({
        key: 'overdue-evals',
        severity: 'destructive',
        icon: Clock,
        label: t('pages.clientDashboard.alerts.overdueEvals', {
          count: overdueEvals.length,
          defaultValue: '{{count}} evaluation request(s) pending HM Advisory > 7 days',
        }),
        to: '/client/requests',
      });
    }

    // 2. Overdue onboarding: stuck on same stage > 14 days
    const overdueOnboardings = data.recruitments.filter((p) => {
      const stage = p.stage || 0;
      if (stage <= 0 || stage >= 11) return false;
      const history = p.stage_history || [];
      const last = history[history.length - 1];
      const enteredAt = last?.entered_at || p.updated_at || p.created_at;
      return (DAYS(enteredAt) || 0) > 14;
    });
    if (overdueOnboardings.length > 0) {
      out.push({
        key: 'overdue-onboardings',
        severity: 'warning',
        icon: AlertTriangle,
        label: t('pages.clientDashboard.alerts.overdueOnboardings', {
          count: overdueOnboardings.length,
          defaultValue: '{{count}} onboarding process(es) stuck on same stage > 14 days',
        }),
        to: '/client/recruitment',
      });
    }

    // 3. Training programs nearing completion — from /talenttrain/alerts
    const trainCount = data.trainingAlerts?.count || 0;
    if (trainCount > 0) {
      out.push({
        key: 'training-alerts',
        severity: 'info',
        icon: GraduationCap,
        label: t('pages.clientDashboard.alerts.trainingAlerts', {
          count: trainCount,
          defaultValue: '{{count}} training alert(s) require attention',
        }),
        to: '/client/talenttrain',
      });
    }

    // 4. Pending requests requiring attention — any pending evaluation request
    const pendingAttn = data.evalReqs.filter((r) => r.status === 'pending').length;
    if (pendingAttn > 0) {
      out.push({
        key: 'pending-attn',
        severity: 'primary',
        icon: Bell,
        label: t('pages.clientDashboard.alerts.pendingAttn', {
          count: pendingAttn,
          defaultValue: "{{count}} request(s) awaiting the consulting team's review",
        }),
        to: '/client/requests',
      });
    }

    // 5. P0 UAT 2026-08-11 — candidacies where the pending action belongs to
    // the AUTHENTICATED CLIENT: report delivered, no outcome recorded, process
    // still open (server-side `awaiting_client_decision`). Resolves once the
    // decision is recorded or the process closes. Only shown to roles that
    // can record the decision (client_admin / client_hr).
    const canDecide = ['client_admin', 'client_hr'].includes(user?.role);
    const awaitingMyDecision = (data.tcWorkspace?.processes || [])
      .filter((p) => p.awaiting_client_decision).length;
    if (canDecide && awaitingMyDecision > 0) {
      out.push({
        key: 'awaiting-your-decision',
        severity: 'primary',
        icon: Bell,
        label: t('pages.clientDashboard.alerts.awaitingYourDecision', {
          count: awaitingMyDecision,
          defaultValue: '{{count}} candidacy(ies) awaiting your decision',
        }),
        to: '/client/talentcheck',
      });
    }

    return out;
  }, [data, t, i18n.language, user]);

  // ------------ Suite status panel ---------------------------------------
  const suiteRows = useMemo(() => {
    if (!data || !metrics) return [];
    const reportsBySuite = new Map();
    for (const r of data.reports) {
      const code = r.suite_code;
      if (!code) continue;
      if (!reportsBySuite.has(code)) reportsBySuite.set(code, []);
      reportsBySuite.get(code).push(r);
    }
    return metrics.activeSuites.map((code) => {
      const reports = reportsBySuite.get(code) || [];
      let lastActivity = reports
        .map((r) => r.released_at)
        .filter(Boolean)
        .sort()
        .pop();
      let participants = new Set(reports.map((r) => r.user_id).filter(Boolean)).size;
      // Founder P1 2026-09 — metrics from REAL suite activity (assignments,
      // training actions, released reports) computed server-side.
      const act = (data.suiteActivity || {})[code];
      if (act) {
        participants = Math.max(participants, act.participants || 0);
        lastActivity = [lastActivity, act.last_activity].filter(Boolean).sort().pop();
      }
      // CLIMARA Closeout FIX 3 — CLIMARA state derives from live campaigns
      // (participation + latest activity), not just released reports.
      if (code === 'climara' && (data.climaraCamps || []).length) {
        participants = data.climaraCamps.reduce((n, c) => n + (c.kpis?.completed || 0), 0);
        const campAct = data.climaraCamps
          .map((c) => c.kpis?.last_activity_at || c.latest_event?.at)
          .filter(Boolean)
          .sort()
          .pop();
        lastActivity = [lastActivity, campAct].filter(Boolean).sort().pop();
      }
      return {
        code,
        name: SUITE_NAMES[code] || code,
        status: 'active',
        lastActivity,
        participants,
        path: SUITE_PATHS[code] || '/client/suites',
      };
    });
  }, [data, metrics]);

  // ------------ Actividad reciente (real tenant events, server-backed) -----
  const ACTIVITY_META = {
    evaluation_assigned:  { icon: ClipboardList, key: 'evaluationAssigned' },
    evaluation_completed: { icon: CheckCircle2,  key: 'evaluationCompleted' },
    training_assigned:    { icon: GraduationCap, key: 'trainingAssigned' },
    training_completed:   { icon: GraduationCap, key: 'trainingCompleted' },
    recognition_created:  { icon: Sparkles,      key: 'recognitionCreated' },
    exit_case_created:    { icon: Briefcase,     key: 'exitCaseCreated' },
    exit_case_closed:     { icon: Briefcase,     key: 'exitCaseClosed' },
    report_released:      { icon: FileText,      key: 'reportReleased' },
  };
  const ACTIVITY_LINKS = {
    evaluation_assigned: '/client/talentmap', evaluation_completed: '/client/talentmap',
    training_assigned: '/client/talenttrain', training_completed: '/client/talenttrain',
    recognition_created: '/client/talentheart',
    exit_case_created: '/client/talentexit', exit_case_closed: '/client/talentexit',
    report_released: '/client/reports',
  };
  const recentActivity = useMemo(() => {
    if (!data) return [];
    const isES = !(i18n.language || '').startsWith('en');
    const items = [];
    // Founder UAT 2026-06 — real operational events from /client/recent-activity.
    (data.serverActivity || []).forEach((e) => {
      const meta = ACTIVITY_META[e.kind];
      if (!meta || !e.at) return;
      const suite = SUITE_NAMES[e.suite_code] || e.suite_code || '';
      items.push({
        at: e.at,
        icon: meta.icon,
        label: t(`pages.clientDashboard.activity.kinds.${meta.key}`, {
          subject: e.subject || '—',
          suite,
          defaultValue: `${suite} · ${e.subject || ''}`,
        }),
        to: SUITE_PATHS[e.suite_code] || ACTIVITY_LINKS[e.kind] || '/client/reports',
      });
    });
    (data.clientNotifs || []).forEach((n) => {
      if (!n.created_at) return;
      items.push({
        at: n.created_at,
        icon: Bell,
        label: (isES ? n.title : (n.title_en || n.title)) || t('pages.clientDashboard.activity.notification', { defaultValue: 'Notificación' }),
        to: n.link || '/client/reports',
      });
    });
    return items
      .filter((x) => x.at)
      .sort((a, b) => String(b.at).localeCompare(String(a.at)))
      .slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, i18n.language, t]);

  // ------------ Executive analytics (Founder UAT 2026-06, real data only) ---
  const analytics = useMemo(() => {
    if (!data) return null;
    // A. Workforce by department (donut)
    const deptCount = new Map();
    (data.employees || []).forEach((u) => {
      const dn = u.department_name_es || u.department_name
        || t('pages.clientDashboard.analytics.noDepartment', { defaultValue: 'Sin departamento' });
      deptCount.set(dn, (deptCount.get(dn) || 0) + 1);
    });
    const byDepartment = Array.from(deptCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));

    // B. Suite activity — real participants per suite (usage, not contracting)
    const suiteUsage = Object.entries(data.suiteActivity || {})
      .map(([code, v]) => ({ name: SUITE_NAMES[code] || code, value: v?.participants || 0 }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    // C. Evaluation status from real assignments — CTH-003: wellbeing survey
    // invitations are NOT evaluations; exclude them so cards and charts agree.
    const stat = { requested: 0, in_progress: 0, submitted: 0 };
    (data.assignmentsOverview || []).forEach((a) => {
      if (a.survey_id) return;
      if (a.status === 'completed') { stat.submitted += 1; return; }
      if (a.status in stat) stat[a.status] += 1;
    });
    const evalStatus = [
      { key: 'requested', name: t('pages.clientDashboard.analytics.evalAssigned', { defaultValue: 'Asignadas' }), value: stat.requested },
      { key: 'in_progress', name: t('pages.clientDashboard.analytics.evalInProgress', { defaultValue: 'En curso' }), value: stat.in_progress },
      { key: 'submitted', name: t('pages.clientDashboard.analytics.evalCompleted', { defaultValue: 'Completadas' }), value: stat.submitted },
    ];
    const evalTotal = stat.requested + stat.in_progress + stat.submitted;

    // D. Training pipeline from real training actions
    const tr = { planned: 0, in_progress: 0, completed: 0 };
    (data.training?.users || []).forEach((u) => {
      const ac = u.action_counts || {};
      tr.planned += ac.planned || 0;
      tr.in_progress += ac.in_progress || 0;
      tr.completed += ac.completed || 0;
    });
    const trainingStatus = [
      { key: 'planned', name: t('pages.clientDashboard.analytics.trPlanned', { defaultValue: 'Planificadas' }), value: tr.planned },
      { key: 'in_progress', name: t('pages.clientDashboard.analytics.trInProgress', { defaultValue: 'En progreso' }), value: tr.in_progress },
      { key: 'completed', name: t('pages.clientDashboard.analytics.trCompleted', { defaultValue: 'Completadas' }), value: tr.completed },
    ];
    const trainingTotal = tr.planned + tr.in_progress + tr.completed;

    // E. Organizational structure — positions filled vs open (real headcount)
    let filled = 0; let open = 0;
    (data.positions || []).forEach((p) => {
      const occ = p.occupied ?? (p.occupants || []).length ?? 0;
      filled += occ;
      if (p.headcount) open += Math.max(0, p.headcount - occ);
    });
    const linkedIds = new Set();
    (data.positions || []).forEach((p) => (p.occupants || []).forEach((o) => linkedIds.add(o.user_id || o.id || o)));
    const unlinked = (data.employees || []).filter((u) => !linkedIds.has(u.id)).length;
    const org = { filled, open, unlinked, hasPositions: (data.positions || []).length > 0 };

    return { byDepartment, suiteUsage, evalStatus, evalTotal, trainingStatus, trainingTotal, org };
  }, [data, t]);

  // ------------ Render ---------------------------------------------------
  if (loading || !metrics) {
    return (
      <>
        <Topbar
          title={data?.company?.name || t('pages.clientDashboard.title')}
          subtitle={t('pages.clientDashboard.subtitle')}
        />
        <div className="py-16 flex items-center justify-center text-muted-foreground" data-testid="client-dashboard-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      </>
    );
  }

  const orgName = data.company?.name || t('pages.clientDashboard.title');
  // Founder refinement — compact sticky header only: small logo + one-line
  // name. The large identity lives exclusively in the scrollable hero below.
  const orgLogo = data.company?.logo_url ? (
    <div className="h-10 w-10 rounded-lg bg-white border border-border/60 shadow-soft flex items-center justify-center overflow-hidden p-1" data-testid="client-portal-logo">
      <img src={data.company.logo_url} alt={orgName} className="h-full w-full object-contain" />
    </div>
  ) : undefined;

  return (
    <>
      <Topbar
        title={<span className="block whitespace-normal line-clamp-2 leading-snug text-base md:text-lg font-semibold max-w-[380px] md:max-w-[540px] xl:max-w-[700px]" title={orgName} data-testid="topbar-compact-company">{orgName}</span>}
        leading={orgLogo}
        actions={
          <Button
            data-testid="client-dashboard-request-btn"
            size="sm"
            className="bg-gradient-primary hover:opacity-90 gap-1.5"
            onClick={() => setReqOpen(true)}
          >
            <Plus className="h-4 w-4" /> {t('pages.clientDashboard.requestEvaluation')}
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="client-dashboard">
        {/* Founder-approved hero refinement: premium dark band with strong
            client identity. Scrolls away naturally — only the compact Topbar
            is sticky. Greeting is the headline; full name never truncates. */}
        <Card className="relative overflow-hidden border-0 shadow-elevated text-white" data-testid="client-dashboard-welcome">
          {data?.company?.custom_banner?.enabled ? (
            /* Branding sprint — HM-published custom banner (cover crop, focal
               point + readability overlay). Default hero below is untouched. */
            <>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/branding/banner/${data.company.id}?v=${data.company.custom_banner.version || 0}`}
                alt="" aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover"
                style={{ objectPosition: FOCAL_POS[data.company.custom_banner.focal_point] || 'center center' }}
                data-testid="hero-custom-banner"
              />
              <div className={`absolute inset-0 ${OVERLAY_CLASS[data.company.custom_banner.overlay] || OVERLAY_CLASS.medium}`} aria-hidden="true" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0B1E38] via-[#11304F] to-[#1C4B7A]" aria-hidden="true" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_40%,rgba(255,255,255,0.14),transparent_55%)]" aria-hidden="true" />
              {data?.company?.logo_url && (
                <img src={data.company.logo_url} alt="" aria-hidden="true"
                     className="pointer-events-none select-none absolute -right-14 -bottom-24 h-[380px] w-[380px] object-contain opacity-[0.08]"
                     data-testid="welcome-crest-watermark" />
              )}
            </>
          )}
          <div className="relative p-7 lg:p-10 flex flex-wrap items-center gap-8">
            <div className="flex-1 min-w-[280px]">
              <p className="text-white/60 text-xs uppercase tracking-[0.18em]" data-testid="welcome-date">
                {new Date().toLocaleDateString(i18n.language?.startsWith('es') ? 'es' : 'en', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight" data-testid="welcome-greeting">
                {t('pages.clientDashboard.welcome.greeting', {
                  name: (user?.name || '').split(' ')[0] || user?.name,
                  defaultValue: 'Welcome back, {{name}}',
                })}
              </h2>
              <p className="text-white/90 text-base lg:text-lg font-semibold mt-2 leading-snug break-words" data-testid="welcome-company-name">
                {orgName}
              </p>
              <p className="text-white/65 text-sm mt-1.5 max-w-2xl">
                {t('pages.clientDashboard.welcome.execSubtitle', { defaultValue: 'Panel Ejecutivo de Talento' })}
                {' · '}
                {t('pages.clientDashboard.welcome.execMessage', { defaultValue: 'Su resumen de talento está al día.' })}
              </p>
              <div className="flex flex-wrap gap-2 mt-4" data-testid="welcome-quick-stats">
                <span className="inline-flex items-center gap-1.5 text-xs bg-white/10 border border-white/20 rounded-full px-3 py-1 text-white/90 backdrop-blur-sm">
                  <ClipboardList className="h-3.5 w-3.5" />
                  {t('pages.clientDashboard.welcome.pendingChip', {
                    count: metrics.pendingApproval,
                    defaultValue: '{{count}} pending request(s)',
                  })}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs bg-white/10 border border-white/20 rounded-full px-3 py-1 text-white/90 backdrop-blur-sm">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {t('pages.clientDashboard.welcome.reportsChip', {
                    count: metrics.completedEvals,
                    defaultValue: '{{count}} released report(s)',
                  })}
                </span>
              </div>
            </div>
            {data?.company?.logo_url ? (
              <ClientLogo
                src={data.company.logo_url}
                alt={orgName}
                className="hidden sm:inline-flex shrink-0 self-center"
                data-testid="welcome-hero-logo"
              />
            ) : (
              /* Branding sprint — tasteful monogram fallback for clients
                 without an uploaded logo (no fake branding generated). */
              <div className="hidden sm:flex h-32 w-32 lg:h-40 lg:w-40 shrink-0 rounded-full bg-white/10 ring-4 ring-white/15 items-center justify-center" data-testid="welcome-hero-monogram">
                <span className="font-display text-4xl lg:text-5xl font-bold text-white/90">{companyInitials(orgName)}</span>
              </div>
            )}
          </div>
        </Card>

        {/* KPI row — 8 click-through tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="client-dashboard-kpis">
          <KpiCard
            icon={Users}
            label={t('pages.clientDashboard.kpi.employees', { defaultValue: 'Active Employees' })}
            value={metrics.employeeCount}
            tone="primary"
            testid="kpi-employees"
            to="/client/employees"
          />
          <KpiCard
            icon={Building}
            label={t('pages.clientDashboard.kpi.departments', { defaultValue: 'Active Departments' })}
            value={metrics.departmentCount}
            tone="info"
            testid="kpi-departments"
            to="/client/departments"
          />
          <KpiCard
            icon={Grid3X3}
            label={t('pages.clientDashboard.kpi.suites', { defaultValue: 'Active Talent Suites' })}
            value={metrics.activeSuiteCount}
            subValue={data?.company?.suites_catalog_total}
            tone="success"
            testid="kpi-suites"
            to="/client/suites"
          />
          <KpiCard
            icon={ClipboardList}
            label={t('pages.clientDashboard.kpi.pendingEvaluations', { defaultValue: 'Pending Evaluations' })}
            value={metrics.pendingApproval}
            hint={t('pages.clientDashboard.kpi.pendingHint', {
              inProgress: metrics.inProgress,
              defaultValue: 'Pending approval · {{inProgress}} in progress',
            })}
            tone="warning"
            testid="kpi-pending-evals"
            to="/client/requests"
          />
          <KpiCard
            icon={CheckCircle2}
            label={t('pages.clientDashboard.kpi.completedEvaluations', { defaultValue: 'Completed Evaluations' })}
            value={metrics.completedEvals}
            tone="success"
            testid="kpi-completed-evals"
            to="/client/reports"
          />
          <KpiCard
            icon={GraduationCap}
            label={t('pages.clientDashboard.kpi.activeTraining', { defaultValue: 'Active Training Programs' })}
            value={metrics.activeTraining}
            hint={t('pages.clientDashboard.kpi.activeTrainingHint', {
              total: metrics.trainingTotal,
              defaultValue: 'of {{total}} collaborators',
            })}
            tone="primary"
            testid="kpi-active-training"
            to="/client/talenttrain"
          />
          <KpiCard
            icon={Briefcase}
            label={t('pages.clientDashboard.kpi.activeOnboardings', { defaultValue: 'Active Onboardings' })}
            value={metrics.activeOnboardings}
            tone="info"
            testid="kpi-active-onboardings"
            to="/client/talentstart"
          />
          <KpiCard
            icon={Wrench}
            label={t('pages.clientDashboard.kpi.openTalentFix', { defaultValue: 'Open TalentFix Requests' })}
            value={
              metrics.talentFix.state === 'not_contracted' || metrics.talentFix.state === 'unavailable'
                ? '—'
                : metrics.talentFix.open
            }
            hint={
              metrics.talentFix.state === 'not_contracted'
                ? t('pages.clientDashboard.kpi.tfNotContracted', { defaultValue: 'Not contracted' })
                : metrics.talentFix.state === 'unavailable'
                  ? t('pages.clientDashboard.kpi.tfUnavailable', { defaultValue: 'Status unavailable' })
                  : metrics.talentFix.state === 'active'
                    ? t('pages.clientDashboard.kpi.tfActive', { defaultValue: 'Active cases in progress' })
                    : t('pages.clientDashboard.kpi.tfNoActiveCases', { defaultValue: 'No active cases' })
            }
            tone={metrics.talentFix.state === 'active' ? 'warning' : 'muted'}
            testid="kpi-talentfix"
            to="/client/talentfix"
          />
        </div>

        {/* Founder UAT 2026-06 — Executive analytics (real data, no line charts) */}
        {analytics && (
          <div className="space-y-4" data-testid="client-dashboard-analytics">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* A. Workforce by department — donut */}
              <Card className="p-5 shadow-card relative overflow-hidden" data-testid="exec-chart-departments">
                <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-400" aria-hidden="true" />
                <h3 className="font-display font-semibold text-base mb-1">
                  {t('pages.clientDashboard.analytics.deptTitle', { defaultValue: 'Colaboradores por departamento' })}
                </h3>
                {analytics.byDepartment.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    {t('pages.clientDashboard.analytics.noData', { defaultValue: 'Sin datos suficientes todavía.' })}
                  </p>
                ) : (
                  <div className="h-52 flex items-center">
                    <ResponsiveContainer width="52%" height="100%">
                      <PieChart>
                        <Pie data={analytics.byDepartment} dataKey="value" nameKey="name"
                             innerRadius={40} outerRadius={72} paddingAngle={3} stroke="none">
                          {analytics.byDepartment.map((d, i) => (
                            <Cell key={d.name} fill={VIVID[i % VIVID.length]} />
                          ))}
                        </Pie>
                        <ChartTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <ul className="flex-1 space-y-1 pr-1 overflow-auto max-h-48">
                      {analytics.byDepartment.map((d, i) => (
                        <li key={d.name} className="flex items-center gap-1.5 text-xs">
                          <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: VIVID[i % VIVID.length] }} />
                          <span className="flex-1 truncate" title={d.name}>{d.name}</span>
                          <span className="font-semibold tabular-nums">{d.value}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>

              {/* B. Suite usage — horizontal bars (real participants, not contracting) */}
              <Card className="p-5 shadow-card relative overflow-hidden" data-testid="exec-chart-suites">
                <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-500 to-amber-400" aria-hidden="true" />
                <h3 className="font-display font-semibold text-base mb-1">
                  {t('pages.clientDashboard.analytics.suiteTitle', { defaultValue: 'Uso real por suite' })}
                </h3>
                <p className="text-xs text-muted-foreground mb-1">
                  {t('pages.clientDashboard.analytics.suiteSubtitle', { defaultValue: 'Participantes con actividad real' })}
                </p>
                {analytics.suiteUsage.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    {t('pages.clientDashboard.analytics.noSuiteUsage', { defaultValue: 'Aún no hay actividad registrada en las suites.' })}
                  </p>
                ) : (
                  <div className="h-48">
                    <ResponsiveContainer>
                      <BarChart data={analytics.suiteUsage} layout="vertical" margin={{ left: 4, right: 22, top: 4, bottom: 0 }}>
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="name" width={104} tick={{ fontSize: 10 }} />
                        <ChartTooltip />
                        <Bar dataKey="value" radius={[0, 5, 5, 0]} barSize={16}>
                          {analytics.suiteUsage.map((s, i) => (
                            <Cell key={s.name} fill={VIVID[i % VIVID.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>

              {/* C+D. Evaluations + Training pipelines */}
              <Card className="p-5 shadow-card relative overflow-hidden" data-testid="exec-chart-pipelines">
                <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-500 to-pink-400" aria-hidden="true" />
                <h3 className="font-display font-semibold text-base mb-2">
                  {t('pages.clientDashboard.analytics.pipelineTitle', { defaultValue: 'Evaluaciones y capacitación' })}
                </h3>
                {analytics.evalTotal === 0 && analytics.trainingTotal === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    {t('pages.clientDashboard.analytics.noPipeline', { defaultValue: 'Sin evaluaciones ni acciones de capacitación registradas todavía.' })}
                  </p>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                        {t('pages.clientDashboard.analytics.evalHeader', { defaultValue: 'Evaluaciones' })} · {analytics.evalTotal}
                      </p>
                      {analytics.evalStatus.map((s, i) => (
                        <div key={s.key} className="mb-1.5" data-testid={`exec-eval-${s.key}`}>
                          <div className="flex justify-between text-xs mb-0.5">
                            <span>{s.name}</span>
                            <span className="font-semibold tabular-nums">{s.value}</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{
                              width: `${analytics.evalTotal ? Math.round((s.value / analytics.evalTotal) * 100) : 0}%`,
                              backgroundColor: ['#EAB308', '#3B82F6', '#10B981'][i],
                            }} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                        {t('pages.clientDashboard.analytics.trHeader', { defaultValue: 'Capacitación' })} · {analytics.trainingTotal}
                      </p>
                      {analytics.trainingStatus.map((s, i) => (
                        <div key={s.key} className="mb-1.5" data-testid={`exec-train-${s.key}`}>
                          <div className="flex justify-between text-xs mb-0.5">
                            <span>{s.name}</span>
                            <span className="font-semibold tabular-nums">{s.value}</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{
                              width: `${analytics.trainingTotal ? Math.round((s.value / analytics.trainingTotal) * 100) : 0}%`,
                              backgroundColor: ['#F97316', '#3B82F6', '#10B981'][i],
                            }} />
                          </div>
                        </div>
                      ))}
                    </div>
                    {analytics.org.hasPositions && (
                      <div className="pt-1 border-t border-border/60 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="exec-org-structure">
                        <span className="font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('pages.clientDashboard.analytics.orgHeader', { defaultValue: 'Estructura' })}
                        </span>
                        <span>{t('pages.clientDashboard.analytics.orgFilled', { defaultValue: 'Puestos ocupados' })}: <b className="tabular-nums">{analytics.org.filled}</b></span>
                        <span>{t('pages.clientDashboard.analytics.orgOpen', { defaultValue: 'Vacantes' })}: <b className="tabular-nums text-orange-600">{analytics.org.open}</b></span>
                        <span>{t('pages.clientDashboard.analytics.orgUnlinked', { defaultValue: 'Sin puesto formal' })}: <b className="tabular-nums">{analytics.org.unlinked}</b></span>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}

        {/* Hybrid §26–27 — Actividad reciente (real events) + Accesos directos */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="client-dashboard-activity-row">
          <Card className="p-5 shadow-card lg:col-span-2" data-testid="recent-activity-card">
            <h3 className="font-display font-semibold text-base flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-primary" />
              {t('pages.clientDashboard.activity.title', { defaultValue: 'Actividad reciente' })}
            </h3>
            {recentActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4" data-testid="recent-activity-empty">
                {t('pages.clientDashboard.activity.empty', { defaultValue: 'Sin actividad reciente.' })}
              </p>
            ) : (
              <ul className="divide-y divide-border/60">
                {recentActivity.map((a, i) => (
                  <li key={i}>
                    <button onClick={() => navigate(a.to)} data-testid={`recent-activity-item-${i}`}
                            className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-muted/40 rounded-md px-2 transition-colors">
                      <span className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <a.icon className="h-4 w-4" />
                      </span>
                      <span className="flex-1 text-sm text-foreground truncate">{a.label}</span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(a.at).toLocaleDateString(i18n.language?.startsWith('es') ? 'es' : 'en', { day: '2-digit', month: 'short' })}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card className="p-5 shadow-card" data-testid="quick-access-card">
            <h3 className="font-display font-semibold text-base flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-primary" />
              {t('pages.clientDashboard.quickAccess.title', { defaultValue: 'Accesos directos' })}
            </h3>
            <div className="space-y-2">
              <Button className="w-full justify-start gap-2 bg-gradient-primary" onClick={() => setReqOpen(true)} data-testid="qa-request-evaluation">
                <Plus className="h-4 w-4" /> {t('pages.clientDashboard.requestEvaluation')}
              </Button>
              {[
                { to: '/client/org-board', icon: Network, label: t('sidebar.orgBoard', { defaultValue: 'Organigrama' }), tid: 'qa-org-board' },
                { to: '/client/requests', icon: ClipboardList, label: t('pages.clientDashboard.quickAccess.evaluations', { defaultValue: 'Evaluaciones' }), tid: 'qa-evaluations' },
                { to: '/client/reports', icon: FileText, label: t('pages.clientDashboard.quickAccess.reports', { defaultValue: 'Informes' }), tid: 'qa-reports' },
                { to: '/client/suites', icon: Grid3X3, label: t('pages.clientDashboard.quickAccess.suites', { defaultValue: 'Suites' }), tid: 'qa-suites' },
              ].map((s) => (
                <Button key={s.tid} variant="outline" className="w-full justify-start gap-2" onClick={() => navigate(s.to)} data-testid={s.tid}>
                  <s.icon className="h-4 w-4 text-primary" /> {s.label}
                  <ArrowRight className="h-3.5 w-3.5 ml-auto text-muted-foreground" />
                </Button>
              ))}
            </div>
          </Card>
        </div>

        {/* Executive alerts */}
        <Card className="p-5 shadow-card" data-testid="client-dashboard-alerts">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="font-display text-lg font-bold tracking-tight inline-flex items-center gap-2">
                <Bell className="h-4 w-4 text-primary" />
                {t('pages.clientDashboard.alerts.title', { defaultValue: 'Executive alerts' })}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('pages.clientDashboard.alerts.subtitle', { defaultValue: 'Real-time signals from across your organisation' })}
              </p>
            </div>
            <Badge variant="outline" className="text-xs" data-testid="alerts-count">
              {alerts.length}
            </Badge>
          </div>
          {alerts.length === 0 ? (
            <div className="py-6 text-sm text-muted-foreground text-center" data-testid="alerts-empty">
              <Sparkles className="h-5 w-5 inline mr-1.5 opacity-50" />
              {t('pages.clientDashboard.alerts.empty', { defaultValue: 'No alerts. Everything is on track.' })}
            </div>
          ) : (
            <ul className="space-y-2">
              {alerts.map((a) => {
                const Icon = a.icon;
                const tone = a.severity === 'destructive' ? 'bg-destructive/10 text-destructive border-destructive/30'
                  : a.severity === 'warning' ? 'bg-warning/10 text-warning border-warning/30'
                  : a.severity === 'info' ? 'bg-info/10 text-info border-info/30'
                  : 'bg-primary/10 text-primary border-primary/30';
                return (
                  <li key={a.key} data-testid={`alert-${a.key}`}>
                    <div className={`w-full flex items-center gap-3 p-3 rounded-lg border ${tone} transition-smooth`}>
                      <button
                        type="button"
                        onClick={async () => {
                          if (a.nid) { try { await api.post(`/client/notifications/${a.nid}/view`); } catch { /* noop */ } }
                          navigate(a.to);
                        }}
                        className="flex-1 flex items-center gap-3 text-left focus:outline-none focus:ring-2 focus:ring-primary rounded"
                        data-testid={a.nid ? `alert-open-${a.nid}` : undefined}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="flex-1 text-sm font-medium">{a.label}</span>
                        {a.isNew && (
                          <span className="text-xs font-bold uppercase bg-amber-200 text-amber-900 rounded-full px-1.5 py-0.5" data-testid={`alert-new-${a.nid}`}>
                            {t('pages.clientDashboard.alerts.new', { defaultValue: 'NUEVO' })}
                          </span>
                        )}
                        <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                      </button>
                      {a.dismissible && (
                        <button
                          type="button"
                          aria-label={t('pages.clientDashboard.alerts.dismiss', { defaultValue: 'Descartar' })}
                          onClick={async () => {
                            try { await api.post(`/client/notifications/${a.nid}/dismiss`); await load(); } catch { /* noop */ }
                          }}
                          className="shrink-0 opacity-60 hover:opacity-100"
                          data-testid={`alert-dismiss-${a.nid}`}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Suite status panel */}
        <Card className="p-5 shadow-card" data-testid="client-dashboard-suites">
          <div className="mb-4">
            <h2 className="font-display text-lg font-bold tracking-tight inline-flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              {t('pages.clientDashboard.suites.title', { defaultValue: 'Suite status' })}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t('pages.clientDashboard.suites.subtitle', { defaultValue: 'Activity across your contracted Talent Suites' })}
              {data?.company?.global_ethics_provisioned && (
                <span className="block mt-0.5 text-xs text-muted-foreground" data-testid="suites-ethics-note">
                  {t('pages.clientDashboard.suites.ethicsNote', { defaultValue: 'Global Ethics is provisioned separately and not included in the suite count.' })}
                </span>
              )}
            </p>
          </div>
          {suiteRows.length === 0 ? (
            <div className="py-6 text-sm text-muted-foreground text-center" data-testid="suites-empty">
              {t('pages.clientDashboard.suites.empty', {
                defaultValue: 'No suites contracted yet. Visit the Suites catalogue to request activation.',
              })}
              <div className="mt-3">
                <Link to="/client/suites">
                  <Button size="sm" variant="outline" data-testid="suites-empty-cta">
                    {t('pages.clientDashboard.suites.openCatalog', { defaultValue: 'Open suite catalogue' })}
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <Table data-testid="suites-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pages.clientDashboard.suites.th.suite', { defaultValue: 'Suite' })}</TableHead>
                  <TableHead>{t('pages.clientDashboard.suites.th.status', { defaultValue: 'Status' })}</TableHead>
                  <TableHead>{t('pages.clientDashboard.suites.th.lastActivity', { defaultValue: 'Last activity' })}</TableHead>
                  <TableHead className="text-right">
                    {t('pages.clientDashboard.suites.th.participants', { defaultValue: 'Participants' })}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suiteRows.map((s) => (
                  <TableRow
                    key={s.code}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => navigate(s.path)}
                    data-testid={`suite-row-${s.code}`}
                  >
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>
                      <Badge className="bg-success/15 text-success border-success/30">
                        {t('pages.clientDashboard.suites.statusActive', { defaultValue: 'Active' })}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground tabular-nums">
                      {fmtDate(s.lastActivity)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{s.participants}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <ClientEvaluationRequestDialog open={reqOpen} onOpenChange={setReqOpen} onCreated={load} />
    </>
  );
}
